// @flow

import {
  getActivePdbId,
  getColorsByPackage,
} from '../../../pdb/selectors';
import {
  getReducer,
} from './main';
import {
  getActivePackageId, getActivePackage,
} from './package';
import type { State as ConfigState } from '../../types';
import type { State as PdbState } from '../../../pdb/types';
import type { OrderFormState } from '../../../orderForm/types';
import type { Color, PackageColor } from '../../../../types/pdb';
import type { State as RouterState } from '../../../router/types';

type GlobalState = {
  configuration: ConfigState,
  pdb: PdbState,
  orderForm: OrderFormState,
  router: RouterState,
};

/**
 * We export the function to get all colors for a car here, since we do that for
 * all actives. The reason is that some car properties depend on choices of other
 * car properties.
 */
export const getColors = (state: GlobalState, pdbId?: ?string): Array<PackageColor> => {
  const activePackage = getActivePackageId(state, pdbId);
  return getColorsByPackage(state, pdbId, activePackage);
};

export const hasColors = (state: GlobalState, pdbId?: ?string): boolean => (
  getColors(state, pdbId).length > 0
);

export const getColorFromActives = (state: GlobalState, pdbId?: ?string) => {
  const selectedPdbId = pdbId || getActivePdbId(state);
  const { activeColor } = getReducer(state);
  return activeColor[selectedPdbId] || null;
};

type ColorId = $PropertyType<Color, 'id'>;
export const getActiveColorId = (state: GlobalState, pdbId?: ?string): ?ColorId => {
  const value = getColorFromActives(state, pdbId);
  return typeof value === 'number' ? value : null;
};

export const getActiveColor = (state: GlobalState, pdbId?: ?string): ?PackageColor => {
  const colors = getColors(state, pdbId);
  if (!colors) return null;
  const active = getActiveColorId(state, pdbId);
  return active ? colors.find(x => x.id === active) : null;
};

export const hasAlternativeColors = (state: GlobalState, pdbId?: ?string): boolean => {
  const colors = getColors(state, pdbId);
  const activeColor = getActiveColor(state);
  if (!activeColor) {
    return false;
  }
  return colors.filter(color => color.id !== activeColor.id).length > 0;
};

type ColorName = $PropertyType<Color, 'name'>;

export const getActiveColorName = (state: GlobalState, pdbId?: ?string): ?ColorName => {
  const color = getActiveColor(state, pdbId);
  return color ? color.name : null;
};

export const getActiveStockId = (state: GlobalState, pdbId?: ?string): number => {
  const colorId = getActiveColorId(state, pdbId);
  if (!colorId) return -1;

  const pack = getActivePackage(state, pdbId);
  if (!pack) return -1;

  const mappedColor = pack.mapped.colors.find(c => c.model_color_id === colorId);
  if (!mappedColor) return -1;

  return mappedColor.stock_id;
};

export default null;
